import {
  Button,
  Card,
  Empty,
  Result,
  Skeleton,
  Space,
  Table,
  Typography,
} from "antd";
import type { TableColumnsType } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { adminApi } from "@/api/client";

type Row = Record<string, unknown>;

export function OperatorsPage() {
  const { t } = useTranslation();

  const op = useQuery({
    queryKey: ["admin", "operators"],
    queryFn: async () => {
      const { data, response } = await adminApi.GET("/admin/v1/operators");
      if (!response.ok || !data?.items)
        throw new Error(`operators:${response.status}`);
      return data.items as Row[];
    },
  });

  const columns: TableColumnsType<Row> = keysOfFirst(op.data).map((k) => ({
    title: k,
    dataIndex: k,
    ellipsis: true,
    render: (v: unknown) => String(v ?? "—"),
  }));

  return (
    <Card title={t("operators.title")}>
      <Typography.Paragraph>{t("operators.subtitle")}</Typography.Paragraph>
      {op.error ? (
        <Result
          status="error"
          title={t("operators.error")}
          subTitle={
            op.error instanceof Error ? op.error.message : String(op.error)
          }
          extra={
            <Button type="primary" onClick={() => op.refetch()}>
              {t("common.retry")}
            </Button>
          }
        />
      ) : op.isPending ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <Table<Row>
            loading={op.isPending}
            dataSource={(op.data ?? []) as Row[]}
            columns={columns.length ? columns : [{ title: "—", render: () => "—" }]}
            pagination={false}
            locale={{
              emptyText: <Empty description={t("operators.empty")} />,
            }}
          />
        </Space>
      )}
    </Card>
  );
}

function keysOfFirst(rows: Row[] | undefined): string[] {
  const first = rows?.[0];
  if (!first) return [];
  return Object.keys(first).slice(0, 12);
}
